import { Dialog, DialogActions, DialogContent, DialogTitle, type DialogProps } from "@mui/material";
import { useEffect, useRef, type ReactNode, type RefObject } from "react";
import { layoutTokens } from "@/shared/theme";

export interface DialogShellProps extends Omit<DialogProps, "children" | "title"> {
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  busy?: boolean;
  initialFocusRef?: RefObject<HTMLElement>;
}

/** Focus-safe title/content/actions shell shared by every application dialog. */
export const DialogShell = ({
  title,
  children,
  actions,
  busy = false,
  initialFocusRef,
  onClose,
  open,
  ...props
}: DialogShellProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => (initialFocusRef?.current ?? contentRef.current)?.focus());
    return () => cancelAnimationFrame(frame);
  }, [initialFocusRef, open]);
  return (
    <Dialog
      {...props}
      open={open}
      disableEscapeKeyDown={busy || props.disableEscapeKeyDown}
      onClose={busy ? undefined : onClose}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent ref={contentRef} tabIndex={-1} aria-busy={busy || undefined} sx={{ px: layoutTokens.pagePadding }}>
        {children}
      </DialogContent>
      {actions && <DialogActions sx={{ px: layoutTokens.pagePadding }}>{actions}</DialogActions>}
    </Dialog>
  );
};
